import React from 'react';
import { Trophy, Target, Activity, Brain, Calendar, DollarSign } from 'lucide-react';

const ChallengesSection: React.FC = () => {
  const challenges = [
    {
      title: '21-Day Consistency Challenge',
      icon: Calendar,
      duration: '3 weeks',
      goal: 'Complete 3 workouts per week for 3 weeks',
      reward: 'Branded T-shirt & social media spotlight',
      motivation: 'Focus on building the habit, not perfection',
      color: 'from-purple-600 to-pink-600'
    },
    {
      title: 'Perfect Posture Challenge',
      icon: Target,
      duration: '30 days',
      goal: 'Daily posture drills (5-10 min) with tracked improvements',
      reward: '$50 Credit',
      motivation: 'Show "before & after" photos of posture alignment',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      title: 'Steps & Movement Challenge',
      icon: Activity,
      duration: '30 days',
      goal: 'Hit 8,000-10,000 steps per day',
      reward: '$50 Credit',
      motivation: 'Helps busy clients stay moving outside training',
      color: 'from-green-600 to-emerald-600'
    },
    {
      title: 'Mind-Body Reset Challenge',
      icon: Brain,
      duration: '4 weeks',
      goal: '3 workouts + 3 stretch sessions + 2 meditations weekly',
      reward: '$50 Credit',
      motivation: 'Holistic approach, not just physical training',
      color: 'from-orange-600 to-red-600'
    }
  ];

  return (
    <section id="challenges" className="py-20 bg-gradient-to-b from-black to-dark">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <Trophy className="text-primary mx-auto mb-4" size={48} />
          <h2 className="font-bebas text-5xl md:text-7xl text-white mb-4 tracking-wider">
            FITNESS <span className="text-primary">CHALLENGES</span>
          </h2>
          <p className="font-montserrat text-gray-400 max-w-2xl mx-auto">
            Push your limits, stay motivated, and earn rewards. Join our community challenges 
            designed to help you build lasting habits and achieve real results.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {challenges.map((challenge, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden bg-black/50 backdrop-blur-sm border-2 border-gray-800 hover:border-primary transition-all duration-500"
            >
              {/* Gradient Background */}
              <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${challenge.color}`}></div>
              
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <challenge.icon className="text-primary" size={32} />
                  <span className="bg-primary/20 text-primary px-3 py-1 text-sm font-montserrat font-semibold rounded-full">
                    {challenge.duration}
                  </span>
                </div>

                <h3 className="font-bebas text-2xl text-white mb-4 tracking-wider">
                  {challenge.title}
                </h3>

                <div className="space-y-4 text-gray-300">
                  <div>
                    <span className="font-montserrat font-semibold text-white">Goal: </span>
                    <span className="font-montserrat text-sm">{challenge.goal}</span>
                  </div>

                  <div>
                    <span className="font-montserrat font-semibold text-white">Motivation: </span>
                    <span className="font-montserrat text-sm italic">{challenge.motivation}</span>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="font-montserrat font-semibold text-primary">
                        Reward:
                      </span>
                      <span className="font-bebas text-xl text-white">
                        {challenge.reward}
                      </span>
                    </div>
                  </div>
                </div>

                <button className="mt-6 w-full py-3 border-2 border-primary text-primary hover:bg-primary hover:text-white font-montserrat font-bold tracking-wider transition-all duration-300">
                  JOIN CHALLENGE
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Community Stats */}
        <div className="mt-16 bg-black/30 backdrop-blur-sm p-8">
          <h3 className="font-bebas text-3xl text-center text-primary mb-8 tracking-wider">
            COMMUNITY ACHIEVEMENTS
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="font-bebas text-4xl text-white">127</div>
              <div className="font-montserrat text-sm text-gray-400">Active Challengers</div>
            </div>
            <div>
              <div className="font-bebas text-4xl text-white">892</div>
              <div className="font-montserrat text-sm text-gray-400">Challenges Completed</div>
            </div>
            <div>
              <div className="font-bebas text-4xl text-white">$4,460</div>
              <div className="font-montserrat text-sm text-gray-400">Credits Earned</div>
            </div>
            <div>
              <div className="font-bebas text-4xl text-white">98%</div>
              <div className="font-montserrat text-sm text-gray-400">Success Rate</div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-400 font-montserrat mb-6">
            Ready to challenge yourself and transform your fitness journey?
          </p>
          <button 
            onClick={() => {
              const element = document.querySelector('#booking');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-primary hover:bg-red-600 text-white font-montserrat font-bold px-8 py-4 text-lg tracking-wider transition-all duration-300 transform hover:scale-105"
          >
            START YOUR CHALLENGE TODAY
          </button>
        </div>
      </div>
    </section>
  );
};

export default ChallengesSection;